/**
 * Outreach landing context (item #24/25 of the launch-readiness audit).
 *
 * Cold-outreach links may include `?ref=coach-outreach` (or `studio`,
 * `actor`, `parent`, `partner`, `casting`). The hub uses this to:
 *   - pre-expand the relevant platform card on HubLauncher
 *   - emphasize the matching microcopy variant
 *   - feed PostHog with cohort attribution alongside UTMs
 *
 * Persisted to sessionStorage so the value survives the SPA navigation
 * between HubLauncher and the per-platform signup pipeline.
 */

export const OUTREACH_KEYS = [
  "studio",
  "coach",
  "actor",
  "parent",
  "partner",
  "casting",
] as const;

export type OutreachKey = (typeof OUTREACH_KEYS)[number];

const STORAGE_KEY = "castready_outreach_ref";

function normalize(raw: string | null): OutreachKey | null {
  if (!raw) return null;
  const lower = raw.toLowerCase().replace(/-outreach$/, "").replace(/^\s+|\s+$/g, "");
  if ((OUTREACH_KEYS as readonly string[]).includes(lower)) {
    return lower as OutreachKey;
  }
  // Map a few sales-team aliases.
  if (lower === "agency" || lower === "manager" || lower === "rep") return "coach";
  if (lower === "family") return "parent";
  if (lower === "cd" || lower === "casting-director") return "casting";
  return null;
}

export function captureOutreachFromUrl(): OutreachKey | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const fromUrl = normalize(url.searchParams.get("ref"));
    if (fromUrl) {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return readStoredOutreach();
  } catch {
    return null;
  }
}

export function readStoredOutreach(): OutreachKey | null {
  if (typeof window === "undefined") return null;
  try {
    return normalize(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Map an outreach key to the HubLauncher platform card it should highlight. */
export const OUTREACH_TO_PLATFORM: Record<OutreachKey, "coach" | "callback-zone" | "castings" | "pro"> = {
  studio: "coach",
  coach: "coach",
  actor: "callback-zone",
  parent: "callback-zone",
  partner: "pro",
  casting: "castings",
};